import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, Eye, ArrowLeft, Tag, ShoppingCartSimple } from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { sanitizeHTML } from '../../utils/sanitize';
import { MagereHeader, MagereFooter } from './MagereSharedLayout';

const API = process.env.REACT_APP_BACKEND_URL;

const StoreBlogPostPage = () => {
  const { storeSlug, postSlug } = useParams();
  const [store, setStore] = useState(null);
  const [pages, setPages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addToCart, itemCount, setIsOpen, setStoreSlug } = useCart();

  useEffect(() => { if (storeSlug) setStoreSlug(storeSlug); }, [storeSlug, setStoreSlug]);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/s/${storeSlug}`).then(r => r.json()),
      fetch(`${API}/api/s/${storeSlug}/pages`).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/s/${storeSlug}/categories`).then(r => r.json()).catch(() => []),
    ]).then(([s, p, c]) => { setStore(s); setPages(p || []); setCategories(c || []); }).catch(() => {});
  }, [storeSlug]);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/s/${storeSlug}/blog/${postSlug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setPost(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [storeSlug, postSlug]);

  const colors = store?.theme_config?.colors || { primary: '#c2185b', secondary: '#4a1942', accent: '#ffc107' };
  const fontFamily = "'Jost', sans-serif";

  const filterLabels = {
    categories: { recettes: 'Recettes', cuisson: 'Cuisson', conseils: 'Conseils', conservation: 'Conservation' },
    meat_types: { boeuf: 'Boeuf', agneau: 'Agneau', poulet: 'Poulet', porc: 'Porc', veau: 'Veau', gibier: 'Gibier' },
    dish_types: { grillades: 'Grillades', barbecue: 'Barbecue', roti: 'Roti', mijote: 'Mijoté', poele: 'Poêle', gastronomique: 'Gastronomique' }
  };

  /* SEO */
  useEffect(() => {
    if (!post || !store) return;
    document.title = `${post.seo_title || post.title} | ${store?.identity?.company_name || store?.name}`;
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', post.seo_description || post.excerpt);
    setMeta('property', 'og:title', post.seo_title || post.title);
    setMeta('property', 'og:description', post.seo_description || post.excerpt);
    setMeta('property', 'og:type', 'article');
    if (post.featured_image) setMeta('property', 'og:image', post.featured_image);
  }, [post, store]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600" />
    </div>
  );

  if (!post) return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center" style={{ fontFamily }}>
      <h1 className="text-2xl font-bold mb-3">Article introuvable</h1>
      <Link to={`/store/${storeSlug}/blog`} className="underline" style={{ color: colors.primary }}>Retour au blog</Link>
    </div>
  );

  const relatedProducts = post.related_products || [];

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden" style={{ fontFamily }} data-testid="blog-post-page">
      <link href="https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;900&display=swap" rel="stylesheet" />

      {/* ── MAGERE HEADER (shared) ── */}
      <MagereHeader store={store} activePage="blog" />

      <div className="h-[50px]" />

      {/* ── HERO IMAGE ── */}
      {post.featured_image && (
        <div className="relative w-full" style={{ height: 'clamp(200px, 35vw, 400px)' }}>
          <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 50%)' }} />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="max-w-[800px] mx-auto">
              <Link to={`/store/${storeSlug}/blog`} className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-[13px] mb-4 transition-colors" data-testid="back-to-blog">
                <ArrowLeft size={14} /> Retour aux conseils
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ── ARTICLE ── */}
      <main className="flex-1 py-10 md:py-14">
        <div className="max-w-[800px] mx-auto px-6">
          {/* Tags */}
          <div className="flex items-center flex-wrap gap-2 mb-5">
            {post.category && (
              <Link to={`/store/${storeSlug}/blog?category=${post.category}`}
                className="px-3 py-1 rounded-full text-[12px] font-bold text-white" style={{ backgroundColor: colors.primary }}>
                {filterLabels.categories[post.category] || post.category}
              </Link>
            )}
            {post.meat_types?.map(mt => (
              <Link key={mt} to={`/store/${storeSlug}/blog?meat_type=${mt}`}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100">
                {filterLabels.meat_types[mt] || mt}
              </Link>
            ))}
            {post.dish_types?.map(dt => (
              <Link key={dt} to={`/store/${storeSlug}/blog?dish_type=${dt}`}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100">
                {filterLabels.dish_types[dt] || dt}
              </Link>
            ))}
          </div>

          <h1 className="text-2xl md:text-4xl font-black mb-4" style={{ color: '#1a1a1a', letterSpacing: '-0.02em' }} data-testid="post-title">{post.title}</h1>

          <div className="flex items-center gap-4 mb-8 text-[13px] text-slate-400 border-b border-slate-100 pb-6">
            <span className="flex items-center gap-1.5"><Clock size={14} /> {new Date(post.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
            <span className="flex items-center gap-1.5"><Eye size={14} /> {post.views} vues</span>
          </div>

          {/* Article content */}
          <article className="prose prose-lg max-w-none
            [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:pb-2 [&_h2]:border-b [&_h2]:border-slate-100
            [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-3
            [&_p]:text-[15px] [&_p]:leading-[1.9] [&_p]:text-slate-700 [&_p]:mb-4
            [&_strong]:text-slate-900
            [&_li]:text-[15px] [&_li]:text-slate-700 [&_li]:mb-1
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
            [&_table]:w-full [&_table]:border-collapse [&_table]:mb-4
            [&_td]:p-2 [&_td]:border-b [&_td]:border-slate-100 [&_td]:text-[14px]
            [&_th]:p-2 [&_th]:border-b-2 [&_th]:border-slate-200 [&_th]:text-left [&_th]:text-[13px] [&_th]:font-bold"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(post.content_html) }}
            data-testid="post-content"
          />
          <style>{`
            [data-testid="post-content"] h2 { color: ${colors.primary}; border-color: ${colors.primary}20; }
            [data-testid="post-content"] a { color: ${colors.primary}; text-decoration: underline; }
          `}</style>

          {/* Tags at bottom */}
          {post.tags?.length > 0 && (
            <div className="mt-10 pt-6 border-t border-slate-100">
              <div className="flex items-center flex-wrap gap-2">
                <Tag size={16} className="text-slate-400" />
                {post.tags.map(tag => (
                  <span key={tag} className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Related products */}
          {relatedProducts.length > 0 && (
            <div className="mt-12 pt-8 border-t-2" style={{ borderColor: colors.primary + '30' }} data-testid="related-products">
              <h2 className="text-xl font-bold mb-6" style={{ color: colors.primary }}>
                Produits mentionnés dans cet article
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {relatedProducts.map((p, i) => (
                  <div key={i} className="bg-white border border-slate-100 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                    <Link to={`/store/${storeSlug}/product/${p.slug}`}>
                      <div className="aspect-square bg-slate-50 overflow-hidden">
                        {(p.featured_image || p.images?.[0]) && (
                          <img src={p.featured_image || p.images[0]} alt={p.name}
                            className="w-full h-full object-contain p-2 hover:scale-110 transition-transform duration-500" loading="lazy" />
                        )}
                      </div>
                    </Link>
                    <div className="p-3">
                      <Link to={`/store/${storeSlug}/product/${p.slug}`}>
                        <h3 className="text-[12px] font-semibold text-slate-800 line-clamp-2 mb-1.5 min-h-[32px] hover:underline">{p.name}</h3>
                      </Link>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-[15px] font-bold" style={{ color: colors.primary }}>{parseFloat(p.price).toFixed(2)} €</span>
                          <span className="text-[10px] text-slate-400 block">{(parseFloat(p.price) / (1 + (typeof p.tax_rate === 'number' ? p.tax_rate : 5.5) / 100)).toFixed(2)} € HT</span>
                        </div>
                        {p.stock_status !== 'outofstock' && (
                          <button onClick={() => addToCart({ ...p, storeSlug })}
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:brightness-110 transition-all"
                            style={{ backgroundColor: colors.primary }} data-testid={`add-to-cart-${i}`}>
                            <ShoppingCartSimple size={14} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Back to blog */}
          <div className="mt-12 text-center">
            <Link to={`/store/${storeSlug}/blog`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold hover:brightness-110 transition-all"
              style={{ backgroundColor: colors.primary }} data-testid="back-to-blog-cta">
              <ArrowLeft size={16} /> Tous nos conseils
            </Link>
          </div>
        </div>
      </main>

      {/* ── MAGERE FOOTER (shared) ── */}
      <MagereFooter store={store} pages={pages} categories={categories} />
    </div>
  );
};

export default StoreBlogPostPage;
